<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { VueDraggable } from "vue-draggable-plus";
import { router } from "@inertiajs/vue3";
import AppIcon from "@/Components/Shared/icon/AppIcon.vue";
import AppProductCard, {
    ProductCardVariant,
} from "@/Components/Shared/product/AppProductCard.vue";
import AppButton from "@/Components/Shared/button/AppButton.vue";
import AppText from "@/Components/Shared/text/AppText.vue";
import AppPagination from "@/Components/Shared/pagination/AppPagination.vue";
import TheEditCollectionProductDescriptionForm from "@/Components/App/collections/TheEditCollectionProductDescriptionForm.vue";
import { apiPathUrl, mustBeVerified, isVerified } from "@/Utils/helpers";
import { useModal } from "@/Utils/use-modal";
import { useCollectionSorting } from "@/Composables/useCollectionSorting";

type ProductItem = {
    id: string; // This is actually the hash/public ID
    name: string;
    slug: string;
    image_path?: string;
    collection_description?: string;
    collection_order?: number;
};

type Props = {
    products: ProductItem[];
    meta?: {
        last_page: number;
        links: Array<any>;
    };
    productVariant?: ProductCardVariant;
    author?: string;
    isOrdered?: boolean;
    hideOptions?: boolean;
    showCloseButton?: boolean;
    isOwner?: boolean;
    isManaging?: boolean;
    context?: {
        type: string;
        subType?: string;
        _path: string;
    };
    via?: string;
};

const {
    products = [],
    meta = undefined,
    productVariant = undefined,
    author = undefined,
    isOrdered = false,
    hideOptions = false,
    showCloseButton = true,
    isOwner = false,
    isManaging = false,
    context = undefined,
    via = undefined,
} = defineProps<Props>();

const emit = defineEmits<{
    "order-changed": [
        movedProduct: { id: string; oldIndex: number; newIndex: number },
    ];
    deleteProduct: [product: ProductItem];
}>();

const sortableProducts = ref<ProductItem[]>([...products]);

// Initialize collection sorting composable if we have a collection context
const { isSaving } = context?.subType
    ? useCollectionSorting(context.subType)
    : { isSaving: ref(false) };

const { openModal } = useModal();

// Keep local state in sync with props
watch(
    () => products,
    newProducts => {
        sortableProducts.value = [...newProducts];
    },
    { deep: true },
);

const handleDragUpdate = (event: any) => {
    // Get the moved product and its indices
    const movedProduct = sortableProducts.value[event.newIndex];
    const oldIndex = event.oldIndex;
    const newIndex = event.newIndex;

    if (oldIndex !== newIndex && movedProduct) {
        // Update local order numbers
        const reorderedProducts = sortableProducts.value.map(
            (product, index) => ({
                ...product,
                collection_order: index + 1,
            }),
        );

        sortableProducts.value = reorderedProducts;

        // Emit the specific product that was moved
        emit("order-changed", {
            id: movedProduct.id,
            oldIndex: oldIndex,
            newIndex: newIndex,
        });
    }
};

const handleTopButtonClick = (product: ProductItem) => {
    emit("deleteProduct", product);
};

const editDescription = (product: ProductItem) => {
    if (!isVerified.value) {
        mustBeVerified();
        return;
    }

    if (!context || context.type !== "collection") {
        return;
    }

    openModal({
        component: TheEditCollectionProductDescriptionForm,
        title: "Beschrijving bewerken",
        size: "regular",
        props: {
            product: product,
            collection: context.id,
        },
    });
};
</script>

<template>
    <div>
        <div
            v-if="products.length === 0"
            class="text-center py-12"
        >
            <AppIcon
                name="collection-empty"
                size="large"
                class="mb-2 text-gray-300"
            />
            <p class="text-gray-500">Nog geen producten in deze collectie</p>
        </div>

        <!-- Ordering Info Box -->
        <div
            v-if="isOrdered && isOwner && products.length > 0"
            class="mb-6 p-4 bg-gray-lightest rounded-lg border border-gray-lighter"
        >
            <div class="flex items-start justify-between">
                <div class="flex-1">
                    <h3 class="font-semibold text-gray-dark mb-1">
                        Aangepaste volgorde
                    </h3>
                    <p class="text-sm text-gray">
                        <template v-if="isManaging">
                            Sleep de producten om ze te herordenen. Wijzigingen
                            worden automatisch opgeslagen.
                        </template>
                        <template v-else>
                            Deze collectie heeft een aangepaste volgorde. Klik
                            op "Volgorde beheren" om de volgorde aan te passen.
                        </template>
                    </p>
                </div>
                <div
                    v-if="isManaging && isSaving"
                    class="flex items-center gap-2 text-sm text-gray"
                >
                    <svg
                        class="animate-spin h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            class="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            stroke-width="4"
                        ></circle>
                        <path
                            class="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                    </svg>
                    <span>Opslaan...</span>
                </div>
            </div>

            <!-- Statistics -->
            <div
                v-if="!isManaging"
                class="mt-3 flex gap-4 text-sm"
            >
                <div>
                    <span class="text-gray">Aantal producten:</span>
                    <span class="font-medium text-gray-dark ml-1">{{
                        products.length
                    }}</span>
                </div>
            </div>
        </div>

        <VueDraggable
            v-if="products.length > 0"
            v-model="sortableProducts"
            :animation="200"
            :delay="100"
            :delay-on-touch-only="true"
            handle=".drag-handle"
            ghost-class="opacity-50"
            drag-class="dragging"
            :disabled="!isManaging"
            class="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 md:gap-8"
            @update="handleDragUpdate"
        >
            <div
                v-for="(product, index) in sortableProducts"
                :key="product.id"
                class="relative group"
                :class="{ 'cursor-move': isManaging }"
            >
                <!-- Ordering Wrapper when managing -->
                <div
                    v-if="isManaging && isOrdered"
                    class="relative"
                >
                    <!-- Order Background -->
                    <div
                        class="absolute inset-0 bg-gray-lightest rounded-lg border-2 border-dashed border-gray-light opacity-50"
                    ></div>

                    <!-- Drag Handle -->
                    <div
                        class="drag-handle absolute top-2 left-2 z-20 bg-white rounded-md shadow-lg p-2 cursor-move hover:bg-gray-lightest transition-colors"
                    >
                        <AppIcon
                            name="grip-vertical"
                            class="text-gray-dark"
                        />
                    </div>

                    <!-- Position Badge -->
                    <div
                        class="absolute top-2 right-2 z-20 bg-primary text-white rounded-md px-3 py-1 text-sm font-semibold shadow-lg"
                    >
                        #{{ index + 1 }}
                    </div>

                    <!-- Product Card with padding for order UI -->
                    <div class="relative pt-12 p-2">
                        <AppProductCard
                            :product="product"
                            :variant="productVariant"
                            :author="author"
                            :via="via"
                            :context="context"
                        />
                    </div>
                </div>
                <!-- Normal Product Card when not managing -->
                <template v-else>
                    <div class="relative">
                        <!-- Order indicator when collection is ordered (not managing) -->
                        <div
                            v-if="isOrdered && !isManaging"
                            class="absolute top-0 left-0 z-10 bg-gray-dark text-white rounded-full px-3 py-1 text-sm font-medium shadow-sm"
                        >
                            {{ index + 1 }}
                        </div>

                        <AppProductCard
                            :product="product"
                            :variant="productVariant"
                            :author="author"
                            :via="via"
                            :context="context"
                        >
                            <template
                                v-if="showCloseButton"
                                #topButton
                            >
                                <AppButton
                                    size="xxsmall"
                                    icon-after="cross"
                                    icon-size="xsmall"
                                    @click="handleTopButtonClick(product)"
                                    >Verwijder</AppButton
                                >
                            </template>
                            <template
                                v-if="
                                    context?.type === 'collection' &&
                                    (product.collection_description || isOwner)
                                "
                                #description
                            >
                                <div class="p-2 pt-0">
                                    <div
                                        :class="[
                                            'bg-gray-lightest rounded p-3 text-sm',
                                            isOwner
                                                ? 'cursor-pointer hover:bg-gray-light transition-colors'
                                                : '',
                                        ]"
                                        @click="
                                            isOwner
                                                ? editDescription(product)
                                                : null
                                        "
                                    >
                                        <template
                                            v-if="
                                                product.collection_description
                                            "
                                        >
                                            {{ product.collection_description }}
                                        </template>
                                        <template v-else-if="isOwner">
                                            <span class="text-gray-dark italic">
                                                Klik om een beschrijving toe te
                                                voegen
                                            </span>
                                        </template>
                                    </div>
                                </div>
                            </template>
                        </AppProductCard>
                    </div>
                </template>
            </div>
        </VueDraggable>

        <!-- Pagination -->
        <AppPagination
            v-if="meta"
            :links="meta.links"
            class="mt-8"
        />
    </div>
</template>

<style scoped>
.dragging {
    opacity: 0.5;
    transform: scale(1.05);
    transition: transform 0.2s;
}

.ghost-class {
    opacity: 0.3;
    background-color: var(--color-gray-lighter);
    border-radius: 0.5rem;
    border: 2px dashed var(--color-primary);
}
</style>
